import { Button, message, Select } from 'antd'
import { Input, Modal } from 'antd-v4'
import { inject } from 'mobx-react'
import React, { FC, useContext, useEffect, useState } from 'react'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { axios } from '@getgreenline/homi-shared'
import Endpoints from '../../../../constants/Endpoints'
import { parseErrorMsg } from '../../../../utilities/helpers'
import { IDigitalSignageDevice } from './DevicePage'
import { DigitalSignageOrientation } from './enums'
import { DigitalSignageContext } from './DigitalSignageContext'
import { IDeviceConfig, SignageIds } from './type'

interface IDeviceModalProps {
  onClose: () => void
  currentCompanyStore?: CurrentCompanyStore
  isOpen: boolean
  isEditing: boolean
  device?: IDigitalSignageDevice | null
}

const deviceInitialState = {
  name: '',
  location: 0,
  orientation: DigitalSignageOrientation.unselected,
  registrationCode: '',
}

export const DeviceModal: FC<IDeviceModalProps> = inject('currentCompanyStore')(
  ({ onClose, currentCompanyStore, isEditing, isOpen: opened, device }: IDeviceModalProps) => {
    const locations = currentCompanyStore!.locations || []
    const company = currentCompanyStore?.company
    const [deviceConfig, setDeviceConfig] = useState<IDeviceConfig>(deviceInitialState)
    const [isSaving, setIsSaving] = useState(false)
    const { signages, addDevice } = useContext(DigitalSignageContext)
    const [signageSelected, setSignageSelected] = useState<SignageIds>({ id: '' })
    const [isFormValid, setIsFormValid] = useState(false)

    const orientationValues = [
      DigitalSignageOrientation.horizontal,
      DigitalSignageOrientation.vertical,
      DigitalSignageOrientation.horizontalInverted,
      DigitalSignageOrientation.verticalInverted,
    ]

    const handleSubmit = async () => {
      const matchedLocation = locations.find((location) => location.id === deviceConfig.location)
      if (company?.id && deviceConfig.orientation) {
        const payload = {
          tenantId: company?.id.toString(),
          retailerId: deviceConfig.location.toString(),
          deviceName: deviceConfig.name,
          retailerName: matchedLocation && matchedLocation.name,
          tenantName: company?.name,
          orientation: parseInt(deviceConfig.orientation),
          timezone: matchedLocation?.timezone,
        }
        if (isEditing && deviceConfig.deviceId) {
          const { data } = await axios.put<IDigitalSignageDevice>(
            Endpoints.UPDATE_DIGITAL_SIGNAGE_DEVICE(
              company.id.toString(),
              deviceConfig.location.toString(),
              deviceConfig.deviceId,
            ),
            {
              ...payload,
              signage: signageSelected.id ? [signageSelected] : [],
            },
          )
          addDevice(data)
        } else {
          const { data } = await axios.post<IDigitalSignageDevice>(
            Endpoints.CREATE_DIGITAL_SIGNAGE_DEVICE(
              company?.id.toString(),
              deviceConfig.location.toString(),
            ),
            {
              ...payload,
              shortCode: deviceConfig.registrationCode,
            },
          )
          addDevice(data)
        }
      }
    }
    const saveDevice = async () => {
      setIsSaving(true)
      try {
        await handleSubmit()
        onClose()
      } catch (error) {
        message.error(parseErrorMsg(error))
      } finally {
        setIsSaving(false)
      }
    }

    useEffect(() => {
      if (opened && isEditing && device) {
        setDeviceConfig({
          deviceId: device.deviceId,
          location: parseInt(device?.retailerId),
          name: device?.deviceName,
          orientation: device?.orientation.toString() as DigitalSignageOrientation,
          registrationCode: device?.shortCode,
        })
        setSignageSelected({
          id: (device?.signage && device.signage.length >= 1 && device.signage[0].id) || '',
        })
      } else {
        setDeviceConfig(deviceInitialState)
      }
    }, [opened])

    const validateCustomizeForm = (device: IDigitalSignageDevice) => {
      const nameUpdated = deviceConfig.name !== device?.deviceName
      const locationUpdated =
        deviceConfig.location !== parseInt(device?.retailerId) && deviceConfig.location !== 0
      const orientationUpdated = deviceConfig.orientation !== device?.orientation.toString()
      const shortCodeUpdated = deviceConfig.registrationCode !== device?.shortCode
      const signageUpdated =
        ((device?.signage && device.signage.length > 0 && device.signage[0].id) || '') !==
        signageSelected.id

      return (
        nameUpdated || locationUpdated || orientationUpdated || shortCodeUpdated || signageUpdated
      )
    }

    const validateCreateDeviceForm = (deviceConfig: IDeviceConfig) => {
      const deviceNameEmpty = deviceConfig.name === ''
      const locationSelected = deviceConfig.location !== 0
      const orientationSelected = deviceConfig.orientation !== DigitalSignageOrientation.unselected
      const registrationCodeEmpty = deviceConfig.registrationCode === ''

      return !deviceNameEmpty && locationSelected && orientationSelected && !registrationCodeEmpty
    }

    useEffect(() => {
      if (isEditing && device) {
        setIsFormValid(validateCustomizeForm(device))
      } else {
        setIsFormValid(validateCreateDeviceForm(deviceConfig))
      }
    }, [deviceConfig, signageSelected])

    return (
      <Modal
        visible={true}
        onCancel={onClose}
        title={isEditing ? `Customize ${device?.deviceName}` : 'Register a new device'}
        footer={[
          <Button key='cancel' onClick={onClose}>
            Cancel
          </Button>,
          <Button
            key='save'
            type='primary'
            disabled={!isFormValid}
            loading={isSaving}
            onClick={() => saveDevice()}
          >
            {isEditing ? 'Save' : 'Register Device'}
          </Button>,
        ]}
      >
        <div>
          <label>Device name</label>
          <Input
            name='deviceName'
            value={deviceConfig.name}
            onChange={(event) => {
              event.persist()
              setDeviceConfig((prevState) => ({
                ...prevState,
                name: event.target?.value || '',
              }))
            }}
          />
        </div>
        {!isEditing && (
          <>
            <br />
            <div>
              <label>Location</label>
              <Select<number>
                key={'location'}
                value={deviceConfig.location}
                style={{ width: '100%', marginBottom: 4 }}
                onChange={(value) =>
                  setDeviceConfig((prevState) => ({
                    ...prevState,
                    location: value,
                  }))
                }
              >
                <Select.Option value={0}>Select location</Select.Option>
                {locations.map((location) => {
                  return (
                    <Select.Option key={location.id} value={location.id}>
                      {location.name}
                    </Select.Option>
                  )
                })}
              </Select>
            </div>
          </>
        )}
        <br />
        <div>
          <label>Orientation</label>
          <Select<DigitalSignageOrientation>
            key={'orientation'}
            value={deviceConfig.orientation}
            style={{ width: '100%', marginBottom: 4 }}
            onChange={(value) =>
              setDeviceConfig((prevState) => ({
                ...prevState,
                orientation: value,
              }))
            }
          >
            <Select.Option value={DigitalSignageOrientation.unselected}>
              Select orientation
            </Select.Option>
            {orientationValues.map((orientationValue) => {
              return (
                <Select.Option key={orientationValue} value={orientationValue}>
                  {orientationValue}
                </Select.Option>
              )
            })}
          </Select>
        </div>
        <br />
        {isEditing ? (
          <div>
            <label>Signage</label>
            <Select<string>
              key={'signage'}
              value={signageSelected.id}
              style={{ width: '100%', marginBottom: 4 }}
              onChange={(value) => {
                setSignageSelected({ id: value })
              }}
            >
              <Select.Option value={''}>Select signage</Select.Option>
              {!!signages &&
                signages.map((signage) => {
                  return (
                    <Select.Option key={signage.id} value={signage.id}>
                      {signage.name}
                    </Select.Option>
                  )
                })}
            </Select>
          </div>
        ) : (
          <div>
            <label>Registration code</label>
            <Input
              name='registrationCode'
              value={deviceConfig.registrationCode}
              onChange={(event) => {
                event.persist()
                setDeviceConfig((prevState) => ({
                  ...prevState,
                  registrationCode: event.target?.value || '',
                }))
              }}
            />
          </div>
        )}
      </Modal>
    )
  },
)
